/**
 * Environment Variable Validation
 *
 * Strict validation for required environment variables.
 * All Firebase config must come from .env.local - NO fallbacks, NO hardcoded values.
 * Fails fast if any required variable is missing.
 */

const env = import.meta.env;

export interface FirebaseEnvConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}

/**
 * Required Firebase environment variable keys
 * Single canonical naming: VITE_FIREBASE_*
 */
const REQUIRED_FIREBASE_VARS = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_FIREBASE_MESSAGING_SENDER_ID',
  'VITE_FIREBASE_APP_ID',
] as const;

type FirebaseEnvKey = (typeof REQUIRED_FIREBASE_VARS)[number];

const ENV_KEY_TO_CONFIG: Record<FirebaseEnvKey, keyof FirebaseEnvConfig> = {
  VITE_FIREBASE_API_KEY: 'apiKey',
  VITE_FIREBASE_AUTH_DOMAIN: 'authDomain',
  VITE_FIREBASE_PROJECT_ID: 'projectId',
  VITE_FIREBASE_STORAGE_BUCKET: 'storageBucket',
  VITE_FIREBASE_MESSAGING_SENDER_ID: 'messagingSenderId',
  VITE_FIREBASE_APP_ID: 'appId',
};

/**
 * Validate and return Firebase configuration
 *
 * All config values MUST come from environment variables.
 * NO fallbacks, NO hardcoded values, NO implicit defaults.
 *
 * @throws Error if any required variable is missing
 */
export function getFirebaseConfig(): FirebaseEnvConfig {
  const missingVars: string[] = [];
  const config: Partial<FirebaseEnvConfig> = {};

  // Check each required variable
  for (const envKey of REQUIRED_FIREBASE_VARS) {
    const value = env[envKey];
    if (value && typeof value === 'string' && value.trim() !== '') {
      config[ENV_KEY_TO_CONFIG[envKey]] = value;
    } else {
      missingVars.push(envKey);
    }
  }

  // All vars present - return config
  if (missingVars.length === 0) {
    return config as FirebaseEnvConfig;
  }

  // Missing vars - FAIL FAST (no fallbacks)
  const errorMsg =
    `[Firebase] FATAL: Missing required environment variables:\n` +
    `  ${missingVars.join('\n  ')}\n\n` +
    `All Firebase configuration must be provided via .env.local.\n` +
    `No fallbacks or hardcoded values are allowed.\n\n` +
    `Please create a .env.local file with the required variables.\n` +
    `See .env.example for a template.`;

  // eslint-disable-next-line no-console -- Critical startup error before logger available
  console.error(errorMsg);
  throw new Error(errorMsg);
}

/**
 * Get list of missing Firebase env vars (for diagnostics)
 * Only returns key names, never values
 */
export function getMissingFirebaseVars(): string[] {
  const missing: string[] = [];

  for (const envKey of REQUIRED_FIREBASE_VARS) {
    const value = env[envKey];
    if (!value || typeof value !== 'string' || value.trim() === '') {
      missing.push(envKey);
    }
  }

  return missing;
}

/**
 * Get the list of required Firebase environment variable names
 */
export function getRequiredFirebaseVars(): readonly string[] {
  return REQUIRED_FIREBASE_VARS;
}
